import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Checklist as TasksIcon,
  WarningAmber as OverdueIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTerms } from '../context/TerminologyContext';
import { useConfirmDialog } from './ConfirmDialog';
import ResponsiveTable from './ResponsiveTable';
import { getUserTimezone, formatPlannedDateTime, formatDateInTimezone } from '../utils/formatTime';
import {
  getTaskSettings,
  updateTaskSettings,
  getTasks,
  getTask,
  createTask,
  updateTask,
  updateTaskStatus,
  deleteTask,
  getEmployees,
  getMyManagedEmployees,
  getEmployee,
  getPunchOptions,
  getDepartmentSettings,
  getDepartments,
} from '../api';

// Server response shape for list endpoints in this app isn't fully settled
// between a bare array and a { tasks: [...] } wrapper — accept either so the
// page keeps working regardless of which the backend ships.
const unwrapList = (data, key) => (Array.isArray(data) ? data : (data?.[key] || []));

const optionKeyOf = (o) => `${o.company_id}-${o.affiliate_id || ''}`;

const PRIORITY_LABELS = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };
const PRIORITY_COLORS = { low: 'default', normal: 'info', high: 'warning', urgent: 'error' };

// Format a "YYYY-MM-DD" (optionally "YYYY-MM-DDTHH:mm:ss") date string without
// letting `new Date()` shift it a day via local timezone conversion.
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = {
  title: '',
  description: '',
  assigned_employee_id: '',
  propertyKey: '',
  department_id: '',
  priority: 'normal',
};

function StatusChip({ task }) {
  if (task.status === 'complete') return <Chip size="small" color="success" label="Complete" />;
  if (task.status === 'pending') {
    return (
      <Tooltip title={task.pending_reason || 'Pending'}>
        <Chip size="small" color="warning" label="Pending" />
      </Tooltip>
    );
  }
  if (task.status === 'active') return <Chip size="small" color="primary" label="Active" />;
  return <Chip size="small" label={task.status} />;
}

export default function Tasks() {
  const { user, isAdmin, isProgramAdmin, isPayrollAdmin, isManager, hasPermission } = useAuth();
  const { t } = useTerms();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const timezone = getUserTimezone();
  const canConfigureSettings = isAdmin || isProgramAdmin || hasPermission('can_configure_settings');
  const isManagerOnly = isManager && !isAdmin && !isProgramAdmin && !isPayrollAdmin;
  const canSeeFullTenant = isAdmin || isProgramAdmin || isPayrollAdmin;

  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [tasksEnabled, setTasksEnabled] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('open');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');

  const [employees, setEmployees] = useState([]);
  const [assignmentBlocked, setAssignmentBlocked] = useState(false);
  const [punchOptions, setPunchOptions] = useState([]);
  const [departmentsEnabled, setDepartmentsEnabled] = useState(false);
  const [departments, setDepartments] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [statusDialog, setStatusDialog] = useState(null); // { type: 'complete' | 'pending', taskId }
  const [statusNote, setStatusNote] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');

  // ── Settings ────────────────────────────────────────────────────────
  const loadSettings = useCallback(() => {
    getTaskSettings()
      .then((res) => setTasksEnabled(!!res.data?.tasks_enabled))
      .catch(() => setTasksEnabled(false))
      .finally(() => setSettingsLoaded(true));
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleToggleSettings = async (e) => {
    const enabled = e.target.checked;
    setSettingsSaving(true);
    setSettingsError('');
    try {
      await updateTaskSettings({ tasks_enabled: enabled });
      setTasksEnabled(enabled);
      window.dispatchEvent(new Event('hourtrax:tasks-settings-changed'));
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to update the tasks setting');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── Reference data: assignees, properties, departments ────────────────
  const loadEmployees = useCallback(async () => {
    try {
      if (canSeeFullTenant) {
        const res = await getEmployees();
        setEmployees(res.data || []);
        setAssignmentBlocked(false);
        return;
      }
      // Managers see ONLY their assigned direct reports, plus themselves —
      // never fall back to the full tenant list.
      const res = await getMyManagedEmployees().catch(() => ({ data: [] }));
      const managed = res.data || [];
      let list = managed;
      const selfId = user?.employeeId;
      if (selfId && !managed.some((e) => String(e.id ?? e.employee_id) === String(selfId))) {
        try {
          const selfRes = await getEmployee(selfId);
          if (selfRes.data) list = [...managed, selfRes.data];
        } catch {
          /* own employee record unavailable — falls through to the managed list */
        }
      }
      setEmployees(list);
      // The server allows self-assignment regardless of managed employees —
      // only block New Task when there is truly no one to assign it to.
      setAssignmentBlocked(list.length === 0);
    } catch {
      setEmployees([]);
      setAssignmentBlocked(true);
    }
  }, [canSeeFullTenant, user?.employeeId]);

  const loadPunchOptions = useCallback(() => {
    getPunchOptions().then((res) => setPunchOptions(res.data?.options || [])).catch(() => setPunchOptions([]));
  }, []);

  const loadDepartments = useCallback(() => {
    getDepartmentSettings()
      .then((res) => {
        const enabled = !!res.data?.departments_enabled;
        setDepartmentsEnabled(enabled);
        if (enabled) return getDepartments().then((r) => setDepartments(r.data || []));
        setDepartments([]);
        return undefined;
      })
      .catch(() => { setDepartmentsEnabled(false); setDepartments([]); });
  }, []);

  useEffect(() => {
    if (!tasksEnabled) return;
    loadEmployees();
    loadPunchOptions();
    loadDepartments();
  }, [tasksEnabled, loadEmployees, loadPunchOptions, loadDepartments]);

  // ── Task list ───────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { status: statusFilter };
      if (assigneeFilter) params.assigned_employee_id = assigneeFilter;
      if (propertyFilter) {
        const opt = punchOptions.find((o) => optionKeyOf(o) === propertyFilter);
        if (opt) {
          params.company_id = opt.company_id;
          if (opt.affiliate_id) params.affiliate_id = opt.affiliate_id;
        }
      }
      const res = await getTasks(params);
      setTasks(unwrapList(res.data, 'tasks'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, assigneeFilter, propertyFilter, punchOptions]);

  useEffect(() => {
    if (!tasksEnabled) return;
    loadTasks();
  }, [tasksEnabled, loadTasks]);

  const today = todayStr();

  // ── Create / Edit dialog ────────────────────────────────────────────
  const handleOpenAdd = () => {
    setEditingTask(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (task) => {
    setEditingTask(task);
    const propertyKey = task.company_id ? `${task.company_id}-${task.affiliate_id || ''}` : '';
    setForm({
      title: task.title || '',
      description: task.description || '',
      assigned_employee_id: task.assigned_employee_id ? String(task.assigned_employee_id) : '',
      propertyKey,
      department_id: task.department_id ? String(task.department_id) : '',
      priority: task.priority || 'normal',
    });
    setFormError('');
    setDialogOpen(true);
    setDetailOpen(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setFormError('Title is required');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const opt = punchOptions.find((o) => optionKeyOf(o) === form.propertyKey);
      // The server only treats a KEY AS ABSENT as "leave unchanged" — it only
      // clears a field on an explicit null. `undefined` here would silently
      // no-op every "set this back to blank" edit, so every optional field
      // sends null rather than being omitted. due_date is the one exception:
      // assigners no longer set it at all, and a non-null value is rejected
      // by the server (DUE_DATE_NOT_ALLOWED) — so the key is left out
      // entirely, which leaves any legacy value on an existing task alone.
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        assigned_employee_id: form.assigned_employee_id || null,
        company_id: opt ? opt.company_id : null,
        affiliate_id: opt ? (opt.affiliate_id || null) : null,
        department_id: form.department_id || null,
        priority: form.priority,
      };
      if (editingTask) {
        await updateTask(editingTask.id, payload);
      } else {
        await createTask(payload);
      }
      setDialogOpen(false);
      await loadTasks();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // ── Detail dialog ───────────────────────────────────────────────────
  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await getTask(row.id);
      setDetailTask(res.data);
    } catch (err) {
      setDetailError(err.response?.data?.error || 'Failed to load task');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    try {
      const res = await getTask(id);
      setDetailTask(res.data);
    } catch {
      /* keep the last-known detail if the refresh itself fails */
    }
  };

  const openStatusDialog = (type, taskId) => {
    setStatusDialog({ type, taskId });
    setStatusNote('');
    setStatusError('');
  };

  const submitStatusChange = async () => {
    if (!statusDialog) return;
    if (statusDialog.type === 'pending' && !statusNote.trim()) {
      setStatusError('A reason is required to set a task pending');
      return;
    }
    setStatusSaving(true);
    setStatusError('');
    try {
      const body = statusDialog.type === 'complete'
        ? { status: 'complete', completion_note: statusNote || undefined }
        : { status: 'pending', pending_reason: statusNote };
      await updateTaskStatus(statusDialog.taskId, body);
      setStatusDialog(null);
      await loadTasks();
      if (detailOpen) await refreshDetail(statusDialog.taskId);
    } catch (err) {
      setStatusError(err.response?.data?.error || 'Failed to update task status');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleReopen = async (task) => {
    setDetailError('');
    try {
      await updateTaskStatus(task.id, { status: 'active' });
      await loadTasks();
      await refreshDetail(task.id);
    } catch (err) {
      setDetailError(err.response?.data?.error || 'Failed to reopen task');
    }
  };

  const handleDelete = async (task) => {
    const confirmed = await showConfirm({
      title: 'Delete Task',
      message: `Delete "${task.title}"? This cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!confirmed) return;
    setDetailError('');
    try {
      await deleteTask(task.id);
      setDetailOpen(false);
      await loadTasks();
    } catch (err) {
      setDetailError(err.response?.data?.error || 'Failed to delete task');
    }
  };

  // ── List columns ─────────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      field: 'title',
      headerName: 'Title',
      mobilePrimary: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {row.title}
          {row.due_date && row.due_date < today && row.status !== 'complete' && (
            <Tooltip title="Past due">
              <OverdueIcon color="error" fontSize="inherit" sx={{ fontSize: 16 }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'property',
      headerName: t('location.singular'),
      mobileSecondary: true,
      renderCell: ({ row }) => row.affiliate_name || row.company_name || '—',
    },
    {
      field: 'assigned_employee_name',
      headerName: 'Assignee',
      mobileSecondary: true,
      renderCell: ({ row }) => row.assigned_employee_name || 'Unassigned',
    },
    {
      field: 'due_date',
      headerName: 'Due',
      renderCell: ({ row }) => {
        // Legacy only — nothing on this screen sets it anymore, but old
        // tasks can still carry one.
        if (!row.due_date) return '—';
        const overdue = row.due_date < today && row.status !== 'complete';
        return (
          <Typography component="span" variant="body2" color={overdue ? 'error' : 'inherit'} fontWeight={overdue ? 600 : 400}>
            {formatDateDisplay(row.due_date)}
          </Typography>
        );
      },
    },
    {
      field: 'planned',
      headerName: 'Planned',
      renderCell: ({ row }) => {
        if (row.planned_start) return <Typography variant="body2">{formatPlannedDateTime(row.planned_start, timezone)}</Typography>;
        if (row.needs_planning) return <Chip size="small" color="warning" label="Needs a time" />;
        return '—';
      },
    },
    {
      field: 'priority',
      headerName: 'Urgency',
      renderCell: ({ row }) => (
        <Chip size="small" label={PRIORITY_LABELS[row.priority] || row.priority} color={PRIORITY_COLORS[row.priority] || 'default'} />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <StatusChip task={row} />
          {row.is_late && <Chip size="small" color="error" label="Late" />}
        </Stack>
      ),
    },
    {
      field: 'hours_logged',
      headerName: 'Hours logged',
      renderCell: ({ row }) => (
        <Tooltip title="Total hours from time entries linked to this task.">
          <span>{Number(row.hours_logged || 0).toFixed(2)}h</span>
        </Tooltip>
      ),
    },
  ]), [today, timezone, t]);

  if (!settingsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Off: setup / toggle card ─────────────────────────────────────────
  if (!tasksEnabled) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TasksIcon /> Tasks
        </Typography>
        {settingsError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSettingsError('')}>{settingsError}</Alert>}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Assign work orders and track their hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tasks let admins and managers hand out work orders to employees, track their status
              (Active, Pending, Complete), and see hours logged against each one from time entries
              linked to it. Employees see their assigned tasks under My Tasks and can pick a task
              when clocking in.
            </Typography>
            {canConfigureSettings ? (
              <Tooltip title="Turns on the Tasks and My Tasks pages tenant-wide, and lets employees link a task to a clock-in.">
                <FormControlLabel
                  control={<Switch checked={tasksEnabled} onChange={handleToggleSettings} disabled={settingsSaving} />}
                  label="Enable tasks for this organization"
                />
              </Tooltip>
            ) : (
              <Alert severity="info">Tasks are turned off for this organization. Ask an admin to turn them on.</Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── On: full page ─────────────────────────────────────────────────
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TasksIcon /> Tasks
        </Typography>
        {canConfigureSettings && (
          <Tooltip title="Turn tasks off for this organization. Existing tasks and their logged hours are kept, but Tasks and My Tasks are hidden until re-enabled.">
            <FormControlLabel
              sx={{ ml: 'auto' }}
              control={<Switch size="small" checked={tasksEnabled} onChange={handleToggleSettings} disabled={settingsSaving} />}
              label="Tasks enabled"
            />
          </Tooltip>
        )}
      </Box>

      {settingsError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSettingsError('')}>{settingsError}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Assignee"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All assignees</MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id ?? emp.employee_id} value={emp.id ?? emp.employee_id}>
                {emp.first_name} {emp.last_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('location.singular')}
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{`All ${t('location.plural', { lower: true })}`}</MenuItem>
            {punchOptions.map((o) => (
              <MenuItem key={optionKeyOf(o)} value={optionKeyOf(o)}>
                {o.label}{o.group ? ` — ${o.group}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flexGrow: 1 }} />
          {isManagerOnly && assignmentBlocked && (
            <Typography variant="caption" color="text.secondary">
              No managed employees assigned — contact an admin
            </Typography>
          )}
          <Tooltip title="Create a new task and optionally assign it to someone.">
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                disabled={isManagerOnly && assignmentBlocked}
              >
                New Task
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={tasks}
          onRowClick={openDetail}
          emptyMessage="No tasks match these filters."
        />
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              label="Title"
              required
              fullWidth
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <TextField
              select
              size="small"
              label="Assignee"
              fullWidth
              value={form.assigned_employee_id}
              onChange={(e) => setForm({ ...form, assigned_employee_id: e.target.value })}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id ?? emp.employee_id} value={emp.id ?? emp.employee_id}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('location.singular')}
              fullWidth
              value={form.propertyKey}
              onChange={(e) => setForm({ ...form, propertyKey: e.target.value })}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">{`No ${t('location.singular', { lower: true })}`}</MenuItem>
              {punchOptions.map((o) => (
                <MenuItem key={optionKeyOf(o)} value={optionKeyOf(o)}>
                  {o.label}{o.group ? ` — ${o.group}` : ''}
                </MenuItem>
              ))}
            </TextField>
            {departmentsEnabled && (
              <TextField
                select
                size="small"
                label={t('department.singular')}
                fullWidth
                value={form.department_id}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">{`No ${t('department.singular', { lower: true })}`}</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              size="small"
              label="Urgency"
              fullWidth
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              InputLabelProps={{ shrink: true }}
            >
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        {detailLoading ? (
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </DialogContent>
        ) : detailTask ? (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {detailTask.title}
              <StatusChip task={detailTask} />
            </DialogTitle>
            <DialogContent>
              {detailError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDetailError('')}>{detailError}</Alert>}
              {detailTask.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {detailTask.description}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip size="small" variant="outlined" label={detailTask.affiliate_name || detailTask.company_name || `No ${t('location.singular', { lower: true })}`} />
                <Chip size="small" variant="outlined" label={detailTask.assigned_employee_name || 'Unassigned'} />
                {/* Legacy only — nobody sets this from the UI anymore, but an
                    old task can still carry one, so it's shown read-only. */}
                {detailTask.due_date && (
                  <Chip size="small" variant="outlined" label={`Due ${formatDateDisplay(detailTask.due_date)}`} />
                )}
                <Chip size="small" label={PRIORITY_LABELS[detailTask.priority] || detailTask.priority} color={PRIORITY_COLORS[detailTask.priority] || 'default'} />
                {detailTask.is_late && <Chip size="small" color="error" label="Late" />}
                <Tooltip title="Total hours from time entries linked to this task.">
                  <Chip size="small" variant="outlined" label={`${Number(detailTask.hours_logged || 0).toFixed(2)}h logged`} />
                </Tooltip>
              </Stack>

              {/* Only the assignee plans when — this is read-only here. */}
              {detailTask.planned_start ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Planned for {formatPlannedDateTime(detailTask.planned_start, timezone)}
                  {detailTask.planned_set_by_name && ` · set by ${detailTask.planned_set_by_name}`}
                </Typography>
              ) : detailTask.needs_planning ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Waiting for {detailTask.assigned_employee_name || 'the assignee'} to pick a time
                </Typography>
              ) : null}

              {detailTask.status === 'pending' && detailTask.pending_reason && (
                <Alert severity="warning" sx={{ mb: 2 }}>Pending: {detailTask.pending_reason}</Alert>
              )}
              {detailTask.status === 'complete' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Completed{detailTask.completed_by_username ? ` by ${detailTask.completed_by_username}` : ''}
                  {/* completed_at is a full timestamp, not a plain date — same
                      fix as My Tasks' Recently Completed list: read it in the
                      account's timezone rather than the browser's/UTC's. */}
                  {detailTask.completed_at ? ` on ${formatDateInTimezone(detailTask.completed_at, timezone)}` : ''}
                  {detailTask.completion_note ? ` — ${detailTask.completion_note}` : ''}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Time Entries</Typography>
              {!detailTask.time_entries || detailTask.time_entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No time entries linked to this task yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailTask.time_entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{formatDateDisplay(entry.entry_date)}</TableCell>
                          <TableCell>{entry.employee_name}</TableCell>
                          <TableCell align="right">{Number(entry.hours_worked || 0).toFixed(2)}</TableCell>
                          <TableCell>{entry.work_details || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 3, pb: 2 }}>
              <Button onClick={() => handleOpenEdit(detailTask)}>Edit</Button>
              {detailTask.status === 'active' && (
                <>
                  <Tooltip title="Mark this task done. You can add an optional completion note.">
                    <Button color="success" onClick={() => openStatusDialog('complete', detailTask.id)}>Mark complete</Button>
                  </Tooltip>
                  <Tooltip title="Pause this task — a reason is required and shows on the Pending status chip.">
                    <Button color="warning" onClick={() => openStatusDialog('pending', detailTask.id)}>Set pending</Button>
                  </Tooltip>
                </>
              )}
              {detailTask.status === 'pending' && (
                <>
                  <Tooltip title="Mark this task done. You can add an optional completion note.">
                    <Button color="success" onClick={() => openStatusDialog('complete', detailTask.id)}>Mark complete</Button>
                  </Tooltip>
                  <Tooltip title="Put this task back to Active.">
                    <Button onClick={() => handleReopen(detailTask)}>Reopen</Button>
                  </Tooltip>
                </>
              )}
              {detailTask.status === 'complete' && (
                <Tooltip title="Put this task back to Active.">
                  <Button onClick={() => handleReopen(detailTask)}>Reopen</Button>
                </Tooltip>
              )}
              <Button color="error" onClick={() => handleDelete(detailTask)}>Delete</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogActions>
          </>
        ) : (
          <DialogContent>
            {detailError && <Alert severity="error">{detailError}</Alert>}
          </DialogContent>
        )}
      </Dialog>

      {/* Mark complete / Set pending dialog */}
      <Dialog open={Boolean(statusDialog)} onClose={() => setStatusDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {statusDialog?.type === 'complete' ? 'Mark Complete' : 'Set Pending'}
          <Tooltip title={statusDialog?.type === 'complete'
            ? 'An optional note about how the task went.'
            : 'A reason is required — it shows on the Pending status chip in the list.'}
          >
            <InfoIcon fontSize="small" color="action" sx={{ ml: 1, verticalAlign: 'middle' }} />
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          {statusError && <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={statusDialog?.type === 'complete' ? 'Completion note (optional)' : 'Reason (required)'}
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitStatusChange} disabled={statusSaving}>
            {statusSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
